import { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  makeStyles, tokens, Text, Button, Spinner, MessageBar, MessageBarBody,
} from '@fluentui/react-components';
import { ArrowClockwiseRegular } from '@fluentui/react-icons';
import { AppTopBar } from '../components/AppTopBar.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { usePuedeVerActividad } from '../stores/authStore.js';
import {
  getActividad,
  type ActividadLista,
  type FiltroActividad,
} from '../api/staffActividad.js';
import { haceCuanto } from '../lib/cuandoFue.js';

/**
 * Quién usó OPUS ai en los últimos días — SOLO staff Ecosoft.
 *
 * Es la puerta de entrada: la lista de personas, y desde cada una se entra a ver qué
 * hizo. Hasta ahora eso se averiguaba entrando al servidor a escribir consultas a mano,
 * así que solo lo podía contestar quien supiera hacerlo.
 *
 * Se listan quienes DEJARON RASTRO, no quienes tienen cuenta: un usuario creado por el
 * aprovisionamiento que nunca entró ensuciaría la lista con gente que solo tocó el botón
 * desde OPUS 27 y rebotó.
 *
 * ENTRAR Y REBOTAR SE VEN DISTINTO. Los dos casos se veían igual —mismo nombre, misma
 * empresa, misma hora— y el 2026-08-25 eso se leyó como que alguien había entrado sin
 * tener plan contratado: un agujero de seguridad que no existía, porque la puerta lo había
 * rebotado. Quien no pudo entrar va en rojo y con el motivo a la vista, y el filtro muestra
 * por defecto a quienes SÍ están usando el producto. Cuando el filtro esconde gente, lo
 * dice: una lista recortada en silencio se lee como una lista vacía.
 */

const VENTANAS = [1, 7, 30, 90];

/** Se recuerda la última elección: quien abre esto varias veces al día busca lo mismo. */
const CLAVE_FILTRO = 'supernova:actividad-filtro';

const FILTROS: { valor: FiltroActividad; etiqueta: string }[] = [
  { valor: 'con_plan', etiqueta: 'Con plan' },
  { valor: 'sin_plan', etiqueta: 'Sin plan' },
  { valor: 'todos', etiqueta: 'Todos' },
];

function filtroGuardado(): FiltroActividad {
  try {
    const v = localStorage.getItem(CLAVE_FILTRO);
    if (v === 'todos' || v === 'sin_plan' || v === 'con_plan') return v;
  } catch {
    // Un navegador que no deja guardar preferencias no puede romper la pantalla.
  }
  return 'con_plan';
}

const useStyles = makeStyles({
  page: { minHeight: '100vh', background: 'var(--bg-canvas)' },
  body: { padding: '20px 28px 64px', maxWidth: '1100px', margin: '0 auto' },
  head: { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '18px' },
  small: { fontSize: tokens.fontSizeBase200, color: 'var(--text-secondary)' },
  barra: {
    display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap',
    padding: '12px 14px', marginBottom: '14px',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: tokens.borderRadiusMedium,
  },
  derecha: { marginLeft: 'auto', display: 'flex', gap: '10px', alignItems: 'center' },
  tablaWrap: {
    overflowX: 'auto',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: tokens.borderRadiusMedium,
  },
  tabla: { width: '100%', borderCollapse: 'collapse', fontSize: tokens.fontSizeBase200 },
  th: {
    textAlign: 'left', padding: '10px 12px', whiteSpace: 'nowrap',
    color: 'var(--text-secondary)', fontWeight: 600,
    borderBottom: '1px solid var(--border-subtle)',
  },
  td: {
    padding: '10px 12px', borderBottom: '1px solid var(--border-subtle)',
    color: 'var(--text-primary)', verticalAlign: 'middle',
  },
  fila: {
    cursor: 'pointer',
    ':hover': { background: 'var(--bg-surface-hover, rgba(127,127,127,.06))' },
  },
  num: { fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' },
  vacio: { padding: '28px 16px', textAlign: 'center', color: 'var(--text-secondary)' },

  // El renglón de quien se quedó afuera. El color sale de los tokens del sistema, así
  // que sigue al tema claro/oscuro; el motivo va escrito, porque el color solo no se
  // puede leer (ni con daltonismo, ni en una captura en blanco y negro).
  filaRebotada: {
    background: 'var(--bad-weak)',
    ':hover': { background: 'var(--bad-weak)' },
  },
  motivo: {
    display: 'inline-block',
    marginTop: '3px',
    padding: '1px 7px',
    borderRadius: tokens.borderRadiusSmall,
    fontSize: '11px',
    fontWeight: 600,
    color: 'var(--bad)',
    border: '1px solid var(--bad)',
  },
  aviso: { marginTop: '10px' },
});

export function StaffActividadPage() {
  const styles = useStyles();
  // Ser staff no alcanza: esta pantalla la ven tres personas. Quién, lo decide el
  // servidor. Esto solo evita el viaje en balde — el portero de verdad está en los
  // dos endpoints, porque una pantalla escondida se abre escribiendo la dirección.
  const puedeVer = usePuedeVerActividad();
  const navigate = useNavigate();

  const [dias, setDias] = useState(7);
  const [filtro, setFiltro] = useState<FiltroActividad>(filtroGuardado);
  const [datos, setDatos] = useState<ActividadLista | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(async (d: number, f: FiltroActividad) => {
    setCargando(true);
    try {
      setDatos(await getActividad(d, f));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo leer la actividad');
    } finally {
      setCargando(false);
    }
  }, []);

  const elegirFiltro = useCallback((f: FiltroActividad) => {
    setFiltro(f);
    try {
      localStorage.setItem(CLAVE_FILTRO, f);
    } catch {
      // Que no se pueda recordar la preferencia no impide usarla ahora.
    }
  }, []);

  useEffect(() => {
    void cargar(dias, filtro);
  }, [cargar, dias, filtro]);

  if (!puedeVer) return <Navigate to="/" replace />;

  return (
    <div className={styles.page}>
      <AppTopBar />
      <div className={styles.body}>
        <Breadcrumbs items={[{ label: 'Inicio', to: '/' }, { label: 'Actividad (staff)' }]} />
        <div className={styles.head}>
          <Text size={600} weight="semibold">Quién usó OPUS ai y ci</Text>
          <Text className={styles.small}>
            Las personas que hicieron algo en el período elegido. Al entrar a una se ven sus
            proyectos, en qué quedó cada uno, y el paso a paso de lo que hizo.
          </Text>
        </div>

        {error && (
          <MessageBar intent="error" style={{ marginBottom: '14px' }}>
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}

        <div className={styles.barra}>
          <Text className={styles.small}>Últimos:</Text>
          {VENTANAS.map((d) => (
            <Button
              key={d}
              size="small"
              appearance={d === dias ? 'primary' : 'secondary'}
              onClick={() => setDias(d)}
            >
              {d === 1 ? '1 día' : `${d} días`}
            </Button>
          ))}
          <div className={styles.derecha}>
            <Button
              size="small"
              icon={<ArrowClockwiseRegular />}
              onClick={() => void cargar(dias, filtro)}
              disabled={cargando}
            >
              Actualizar
            </Button>
          </div>
        </div>

        <div className={styles.barra}>
          <Text className={styles.small}>Mostrar:</Text>
          {FILTROS.map((f) => (
            <Button
              key={f.valor}
              size="small"
              appearance={f.valor === filtro ? 'primary' : 'secondary'}
              onClick={() => elegirFiltro(f.valor)}
            >
              {f.etiqueta}
              {datos ? ` (${
                f.valor === 'con_plan' ? datos.conteos.conPlan
                  : f.valor === 'sin_plan' ? datos.conteos.sinPlan
                    : datos.conteos.total
              })` : ''}
            </Button>
          ))}
          <Text className={styles.small}>
            {filtro === 'con_plan'
              ? 'Quienes sí pudieron entrar y usar OPUS ai u OPUS ci.'
              : filtro === 'sin_plan'
                ? 'Quienes lo intentaron y se quedaron afuera.'
                : 'Todos: los que entraron y los que se quedaron afuera.'}
          </Text>
        </div>

        {cargando && !datos ? (
          <Spinner label="Leyendo la actividad…" />
        ) : (
          <div className={styles.tablaWrap}>
            <table className={styles.tabla}>
              <thead>
                <tr>
                  <th className={styles.th}>Persona</th>
                  <th className={styles.th}>Empresa</th>
                  <th className={styles.th}>Plan</th>
                  {/* No es "última vez que entró": es lo último que hizo y quedó
                      registrado. La sesión se mantiene sola mientras trabaja, así que su
                      inicio de sesión puede ser mucho más viejo que esta fecha. */}
                  <th className={styles.th}>Última actividad</th>
                  <th className={styles.th}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {datos?.usuarios.map((u) => (
                  <tr
                    key={u.userId}
                    className={`${styles.fila} ${u.noPudoEntrar ? styles.filaRebotada : ''}`}
                    onClick={() => navigate(`/staff/actividad/${u.userId}`)}
                  >
                    <td className={styles.td}>
                      <div>{u.nombre ?? u.email}</div>
                      {u.nombre && <div className={styles.small}>{u.email}</div>}
                      {u.noPudoEntrar && (
                        <div>
                          <span className={styles.motivo}>
                            {u.porQueNoPudoEntrar}
                            {u.intentosRechazados > 1 && ` · lo intentó ${u.intentosRechazados} veces`}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className={styles.td}>{u.empresa ?? '—'}</td>
                    <td className={styles.td}>
                      {u.plan ?? <span className={styles.small}>sin plan</span>}
                    </td>
                    <td className={`${styles.td} ${styles.num}`}>{haceCuanto(u.ultimaActividad)}</td>
                    <td className={`${styles.td} ${styles.num}`}>{u.eventos}</td>
                  </tr>
                ))}
                {(datos?.usuarios.length ?? 0) === 0 && !cargando && (
                  <tr>
                    <td className={styles.vacio} colSpan={5}>
                      {filtro === 'sin_plan'
                        ? `A nadie le rebotó la entrada en ${dias === 1 ? 'el último día' : `los últimos ${dias} días`}.`
                        : filtro === 'con_plan' && (datos?.conteos.sinPlan ?? 0) > 0
                          ? `Nadie entró en ${dias === 1 ? 'el último día' : `los últimos ${dias} días`}, pero sí hubo intentos que rebotaron: míralos en «Sin plan».`
                          : `Nadie hizo nada en ${dias === 1 ? 'el último día' : `los últimos ${dias} días`}.`}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {filtro !== 'todos' && datos && (
          <MessageBar intent="info" className={styles.aviso}>
            <MessageBarBody>
              {filtro === 'con_plan'
                ? datos.conteos.sinPlan > 0
                  ? `Estás viendo solo a quienes entraron. Hay ${datos.conteos.sinPlan} ${
                      datos.conteos.sinPlan === 1 ? 'persona que intentó' : 'personas que intentaron'
                    } entrar y no ${datos.conteos.sinPlan === 1 ? 'pudo' : 'pudieron'}: ${
                      datos.conteos.sinPlan === 1 ? 'míralo' : 'míralos'
                    } en «Sin plan».`
                  : 'Estás viendo solo a quienes entraron. En este período no rebotó nadie.'
                : `Estás viendo solo a quienes se quedaron afuera. Los ${datos.conteos.conPlan} que sí entraron están en «Con plan».`}
            </MessageBarBody>
          </MessageBar>
        )}

        <Text className={styles.small} style={{ display: 'block', marginTop: '10px' }}>
          Aparece quien dejó rastro, no quien tiene cuenta. Un renglón en rojo no entró: la
          puerta lo rebotó y el motivo está junto a su nombre. Entra al renglón para ver el
          paso a paso de lo que intentó.
        </Text>
      </div>
    </div>
  );
}
